import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Edit3, Save, Plus, Trash2, X } from "lucide-react";
import RechargeDialog from "@/components/RechargeDialog";

export default function MetersTab() {
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ room_number: "", sub_room: "", meter_number: "" });
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meters");
      setMeters(data || []);
    } catch (_) {
      toast.error("Failed to load meters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({
      room_number: m.room_number,
      sub_room: m.sub_room || "",
      meter_number: m.meter_number,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setAdding(false);
    setForm({ room_number: "", sub_room: "", meter_number: "" });
  };

  const save = async () => {
    if (!form.room_number || !form.meter_number) {
      toast.error("Room number and meter number are required");
      return;
    }
    try {
      if (adding) {
        await api.post("/meters", form);
        toast.success("Meter added");
      } else {
        await api.put(`/meters/${editing}`, form);
        toast.success("Meter updated");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this meter entry?")) return;
    try {
      await api.delete(`/meters/${id}`);
      toast.success("Deleted");
      load();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="meters-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading font-black text-3xl tracking-tighter">
            Prepaid Electricity Meters
          </h3>
          <p className="text-sm font-medium text-[#4A4A4A] mt-1">
            Master list of meter numbers per room / sub-room. Residents look these up before recharging.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setAdding(true);
              setEditing(null);
              setForm({ room_number: "", sub_room: "", meter_number: "" });
            }}
            data-testid="meters-add-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <Plus size={16} /> Add Meter
          </button>
          <button
            onClick={() => setRechargeOpen(true)}
            data-testid="meters-recharge-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <Zap size={16} strokeWidth={2.5} /> Recharge
          </button>
        </div>
      </div>

      {adding && (
        <div
          className="border-2 border-[#0A0A0A] rounded-xl p-5 mb-6 bg-[#FFFCE0]"
          style={{ boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)" }}
          data-testid="meter-add-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Room Number" required>
              <input
                data-testid="meter-form-room"
                value={form.room_number}
                onChange={(e) => setForm({ ...form, room_number: e.target.value })}
                className="input-brutal-m"
              />
            </FormField>
            <FormField label="Sub Room">
              <input
                data-testid="meter-form-sub"
                value={form.sub_room}
                onChange={(e) => setForm({ ...form, sub_room: e.target.value })}
                className="input-brutal-m"
              />
            </FormField>
            <FormField label="Meter Number" required>
              <input
                data-testid="meter-form-meter"
                value={form.meter_number}
                onChange={(e) => setForm({ ...form, meter_number: e.target.value })}
                className="input-brutal-m"
              />
            </FormField>
          </div>
          <div className="flex gap-3 mt-4 justify-end">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 font-bold border-2 border-[#0A0A0A] rounded-full bg-white"
            >
              Cancel
            </button>
            <button
              onClick={save}
              data-testid="meter-form-save"
              className="inline-flex items-center gap-2 px-4 py-2 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white"
            >
              <Save size={14} strokeWidth={2.5} /> Save
            </button>
          </div>
        </div>
      )}

      <div className="brutal-card p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-left" data-testid="meters-table">
            <thead className="bg-[#0A0A0A] text-white sticky top-0">
              <tr>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Room</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Sub Room</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Meter Number</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center font-medium text-[#4A4A4A]">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && meters.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-4 py-10 text-center font-medium text-[#4A4A4A]">
                    No meters yet.
                  </td>
                </tr>
              )}
              {meters.map((m) => {
                const isEditing = editing === m.id;
                return (
                  <tr
                    key={m.id}
                    className="border-t-2 border-[#0A0A0A] hover:bg-[#FFFCE0]"
                    data-testid={`meter-row-${m.id}`}
                  >
                    <td className="px-4 py-3 font-bold">
                      {isEditing ? (
                        <input
                          value={form.room_number}
                          onChange={(e) => setForm({ ...form, room_number: e.target.value })}
                          className="input-brutal-m"
                        />
                      ) : (
                        m.room_number
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          value={form.sub_room}
                          onChange={(e) => setForm({ ...form, sub_room: e.target.value })}
                          className="input-brutal-m"
                        />
                      ) : (
                        m.sub_room || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono font-bold">
                      {isEditing ? (
                        <input
                          value={form.meter_number}
                          onChange={(e) => setForm({ ...form, meter_number: e.target.value })}
                          className="input-brutal-m"
                        />
                      ) : (
                        m.meter_number
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        {isEditing ? (
                          <>
                            <button
                              onClick={save}
                              className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white"
                              data-testid={`meter-save-${m.id}`}
                            >
                              <Save size={14} strokeWidth={2.5} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-2 border-2 border-[#0A0A0A] rounded-full bg-white"
                            >
                              <X size={14} strokeWidth={2.5} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(m)}
                              className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600]"
                              data-testid={`meter-edit-${m.id}`}
                            >
                              <Edit3 size={14} strokeWidth={2.5} />
                            </button>
                            <button
                              onClick={() => remove(m.id)}
                              className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white"
                              data-testid={`meter-delete-${m.id}`}
                            >
                              <Trash2 size={14} strokeWidth={2.5} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          onClick={() => setRechargeOpen(true)}
          data-testid="meters-recharge-button-bottom"
          className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
        >
          <Zap size={16} strokeWidth={2.5} /> Recharge
        </button>
      </div>

      <RechargeDialog open={rechargeOpen} onOpenChange={setRechargeOpen} />
      <style>{`
        .input-brutal-m {
          width: 100%;
          padding: 0.55rem 0.85rem;
          border: 2px solid #0A0A0A;
          border-radius: 0.5rem;
          background: white;
          font-weight: 500;
        }
        .input-brutal-m:focus {
          outline: none;
          box-shadow: 0 0 0 2px white, 0 0 0 4px #0A0A0A;
        }
      `}</style>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-[#FF4D4D]">*</span>}
      </span>
      {children}
    </label>
  );
}
